import { useState, useMemo } from "react";
import {
  Holding,
  Cliente,
  Propiedad,
  Usuario,
} from "../../types";

interface AnalisisFormProps {
  holdings: Holding[];
  clientes: Cliente[];
  propiedades: Propiedad[];
  usuarios: Usuario[];
  defaultHoldingId?: number;
  onSubmit: (data: {
    holdingId: number;
    titulo: string;
    descripcion?: string;
    arquitectoId?: number;
    abogadoId?: number;
    propiedadIds: number[];
  }) => void;
  onCancel: () => void;
}

export function AnalisisForm({
  holdings,
  clientes,
  propiedades,
  usuarios,
  defaultHoldingId,
  onSubmit,
  onCancel,
}: AnalisisFormProps) {
  const [holdingId, setHoldingId] = useState(defaultHoldingId || 0);
  const [holdingSearch, setHoldingSearch] = useState("");
  const [showHoldingDropdown, setShowHoldingDropdown] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [arquitectoId, setArquitectoId] = useState(0);
  const [abogadoId, setAbogadoId] = useState(0);
  const [selectedPropiedadIds, setSelectedPropiedadIds] = useState<number[]>([]);
  const [propSearch, setPropSearch] = useState("");

  const arquitectos = usuarios.filter((u) => u.rol === "arquitecto" || u.rol === "admin");
  const abogados = usuarios.filter((u) => u.rol === "abogado" || u.rol === "admin");

  const selectedHolding = holdings.find((h) => h.id === holdingId);
  const holdingClientes = clientes.filter((c) => c.holdingId === holdingId);
  const holdingClienteIds = holdingClientes.map((c) => c.id);
  const holdingPropiedades = propiedades.filter((p) =>
    holdingClienteIds.includes(p.clienteId) && p.estadoPropiedad === "activa"
  );

  const filteredHoldings = useMemo(() => {
    if (!holdingSearch) return holdings;
    const search = holdingSearch.toLowerCase();
    return holdings.filter((h) => h.nombre.toLowerCase().includes(search));
  }, [holdings, holdingSearch]);

  const filteredPropiedades = useMemo(() => {
    if (!propSearch) return holdingPropiedades;
    const search = propSearch.toLowerCase();
    return holdingPropiedades.filter(
      (p) =>
        p.rol.toLowerCase().includes(search) ||
        p.comuna.toLowerCase().includes(search)
    );
  }, [holdingPropiedades, propSearch]);

  const togglePropiedad = (id: number) => {
    setSelectedPropiedadIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    setSelectedPropiedadIds(filteredPropiedades.map((p) => p.id));
  };

  const deselectAll = () => {
    setSelectedPropiedadIds([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      holdingId,
      titulo: titulo.trim(),
      descripcion: descripcion.trim() || undefined,
      arquitectoId: arquitectoId || undefined,
      abogadoId: abogadoId || undefined,
      propiedadIds: selectedPropiedadIds,
    });
  };

  const isValid = holdingId > 0 && titulo.trim().length > 0 && selectedPropiedadIds.length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Holding Selector */}
      <div className="relative">
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Holding *
        </label>
        <div
          className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm cursor-pointer bg-white ${
            defaultHoldingId ? "bg-slate-100 cursor-not-allowed" : ""
          }`}
          onClick={() => !defaultHoldingId && setShowHoldingDropdown(!showHoldingDropdown)}
        >
          {selectedHolding ? (
            <span className="text-slate-800">{selectedHolding.nombre}</span>
          ) : (
            <span className="text-slate-400">Seleccionar Holding...</span>
          )}
        </div>

        {showHoldingDropdown && !defaultHoldingId && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-slate-200">
              <input
                type="text"
                value={holdingSearch}
                onChange={(e) => setHoldingSearch(e.target.value)}
                placeholder="Buscar por nombre..."
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filteredHoldings.map((h) => (
                <div
                  key={h.id}
                  onClick={() => {
                    setHoldingId(h.id);
                    setHoldingSearch("");
                    setShowHoldingDropdown(false);
                    setSelectedPropiedadIds([]);
                  }}
                  className={`px-3 py-2 cursor-pointer hover:bg-slate-100 ${
                    h.id === holdingId ? "bg-blue-50 text-blue-700" : ""
                  }`}
                >
                  <div className="font-medium">{h.nombre}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Titulo */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Titulo del Estudio *
        </label>
        <input
          type="text"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Ej: Estudio Sobretasa Propiedades Centro"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Descripcion */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Descripcion
        </label>
        <textarea
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          rows={2}
          placeholder="Descripcion opcional..."
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>

      {/* Equipo */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Arquitecto
          </label>
          <select
            value={arquitectoId}
            onChange={(e) => setArquitectoId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Sin asignar</option>
            {arquitectos.map((u) => (
              <option key={u.id} value={u.id}>{u.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Abogado
          </label>
          <select
            value={abogadoId}
            onChange={(e) => setAbogadoId(Number(e.target.value))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Sin asignar</option>
            {abogados.map((u) => (
              <option key={u.id} value={u.id}>{u.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ROL Selection */}
      {selectedHolding && (
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-slate-700">
              ROLes a estudiar * ({selectedPropiedadIds.length} seleccionados)
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                Todos
              </button>
              <button
                type="button"
                onClick={deselectAll}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Ninguno
              </button>
            </div>
          </div>

          {holdingPropiedades.length > 10 && (
            <input
              type="text"
              value={propSearch}
              onChange={(e) => setPropSearch(e.target.value)}
              placeholder="Filtrar ROLes por comuna o ROL..."
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
            />
          )}

          <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
            {holdingClientes.map((c) => {
              const clienteProps = filteredPropiedades.filter((p) => p.clienteId === c.id);
              if (clienteProps.length === 0) return null;
              return (
                <div key={c.id}>
                  <div className="px-3 py-1.5 bg-slate-50 text-xs font-medium text-slate-500 border-b border-slate-100">
                    {c.nombre} ({c.rut})
                  </div>
                  {clienteProps.map((p) => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${
                        selectedPropiedadIds.includes(p.id) ? "bg-blue-50" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPropiedadIds.includes(p.id)}
                        onChange={() => togglePropiedad(p.id)}
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">
                        {p.comuna} - ROL {p.rol}
                      </span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
          {selectedPropiedadIds.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">Seleccione al menos un ROL</p>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!isValid}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          Crear Estudio
        </button>
      </div>
    </form>
  );
}
